import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import MainLayout from "@/layouts/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import { MaterialIcon } from "@/components/ui/material-icon";
import { useQuery } from "@tanstack/react-query";
import teamPhoto from "@/assets/team-photo.jpg";
import logo from "@/assets/rsg-white.png";
import AnimatedSection from "@/components/AnimatedSection";
import LazyImage from "@/components/LazyImage";
import ParallaxHero from "@/components/ParallaxHero";
import { GameDevProgressSection } from "@/components/GameDevProgressSection";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { Facebook, Instagram, Youtube, Mail } from "lucide-react";

interface CrewMember {
  id: string;
  name: string;
  photo_url: string | null;
  titles: string[];
  sort_order: number;
}
const XIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Hanging-flag crew card geometry (mirrors PointedSectionBackground)
const FLAG_EDGE: Array<[number, number]> = [
  [0, 14],
  [50, 50],
  [100, 14],
];
const FLAG_BAND = 76;
const FLAG_TRIM = 10;
const FLAG_OVERLAP = 6;

const FLAG_CLIP = `polygon(0% 0%, 100% 0%, ${FLAG_EDGE.map(([x, depth]) => `${x}% calc(100% - ${FLAG_BAND - depth}px)`)
  .reverse()
  .join(", ")})`;

const FLAG_HEM = `M${FLAG_EDGE.map(([x, depth]) => `${x},${depth - FLAG_OVERLAP}`).join(" L")} L${FLAG_EDGE.map(
  ([x, depth]) => `${x},${depth + FLAG_TRIM}`,
)
  .reverse()
  .join(" L")} Z`;

const About = () => {
  const { settings } = useSiteSettings();
  const [crewMembers, setCrewMembers] = useState<CrewMember[]>([]);

  useEffect(() => {
    const fetchCrew = async () => {
      const { data } = await supabase.from("crew_members").select("*").order("sort_order", { ascending: true });
      if (data) setCrewMembers(data);
    };
    fetchCrew();
  }, []);

  const { data: heroSetting } = useQuery({
    queryKey: ["site-setting", "about_hero_image"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "about_hero_image")
        .maybeSingle();
      if (error) throw error;
      return data?.setting_value;
    },
  });

  // Use custom hero image, or fall back to team photo
  const heroImage = heroSetting || teamPhoto;

  const defaultCrew = [
    { id: "1", name: "Kyle Lundy", titles: [], photo_url: null },
    { id: "2", name: "Adam Clement", titles: [], photo_url: null },
    { id: "3", name: "Ross Conner", titles: [], photo_url: null },
  ];

  const displayCrew = crewMembers.length > 0 ? crewMembers : defaultCrew;

  const missionParagraphs = (
    settings.about_mission_text ||
    "At Rogue Skull Games, our mission is to craft inventive and fun board games that elevate your tabletop gaming experience with a rogue twist. Since our inception in 2023, we've set sail to curate a diverse portfolio of board games, ranging from strategic masterpieces to immersive adventures.\n\nOur commitment to design excellence, visual appeal, and high-quality components ensures that every game we create becomes a standout choice for all gamers."
  )
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  const stats = [
    { value: settings.about_stat_1_value || "2023", label: settings.about_stat_1_label || "Founded" },
    { value: settings.about_stat_2_value || "KS", label: settings.about_stat_2_label || "Kansas Based" },
    { value: settings.about_stat_3_value || "3", label: settings.about_stat_3_label || "Founders" },
    { value: settings.about_stat_4_value || "∞", label: settings.about_stat_4_label || "Passion" },
  ];

  const valueCards = [
    {
      num: "01",
      title: settings.about_value_1_title || "Our Style",
      desc:
        settings.about_value_1_text ||
        "We have a soft spot for board games that bring out the competitive spirit. Many of our creations involve an intriguing sabotage component, allowing you to go rogue on your opponent's resources.",
    },
    {
      num: "02",
      title: settings.about_value_2_title || "Our Vision",
      desc:
        settings.about_value_2_text ||
        "Our vision is simple yet ambitious: to create balanced, enjoyable board games that appeal to a wide range of players. We're passionate about delivering memorable gaming experiences.",
    },
    {
      num: "03",
      title: settings.about_value_3_title || "Our Games",
      desc:
        settings.about_value_3_text ||
        "While our first releases immerse players in the heart-pounding world of Battle Royale board games, our creative spirit knows no bounds. We explore various categories for all enthusiasts.",
    },
  ];

  return (
    <MainLayout>
      <Helmet>
        <title>About | Rogue Skull Games</title>
        <meta
          name="description"
          content="Learn about Rogue Skull Games, a Kansas-based indie board game publisher passionate about creating exciting, strategic tabletop experiences."
        />
        <link rel="canonical" href="https://rogueskullgames.com/about" />
        <meta property="og:title" content="About | Rogue Skull Games" />
        <meta
          property="og:description"
          content="Kansas-based indie board game publisher building strategic tabletop experiences."
        />
        <meta property="og:url" content="https://rogueskullgames.com/about" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section with Parallax */}
      <ParallaxHero
        imageUrl={heroImage}
        alt="Rogue Skull Games team"
        className="landing-page-hero"
        overlayClassName="hero-overlay-fade"
        heightClass="min-h-[620px] md:min-h-[600px]"
      >
        {/* Content */}
        <div className="container hero-content z-20">
          <div className="">
            <div className="max-w-5xl">
              <span className="inline-block text-blood-red font-semibold tracking-[0.3em] mb-2 animate-slide-in">
                {settings.about_hero_label || "WHO WE ARE"}
              </span>
              <h1
                className="text-4xl lg:text-6xl text-dark-foreground font-display font-bold leading-[1.1] mb-6 animate-fade-up"
                style={{ animationDelay: "0.1s" }}
              >
                {settings.about_hero_title || "Rogue Skull Games — Indie Board Game Publisher"}
              </h1>
              <p
                className="text-dark-foreground/80 text-lg md:text-xl leading-relaxed max-w-2xl animate-fade-up"
                style={{ animationDelay: "0.2s" }}
              >
                {settings.about_hero_text ||
                  "Founded in September 2023, Rogue Skull Games is the brainchild of three lifelong friends with a shared passion for board games. Our journey began as high school buddies, evolving into a thrilling adventure in tabletop gaming."}
              </p>
            </div>
          </div>
        </div>
      </ParallaxHero>

      <div className="flex flex-col">
        {/* Mission Section */}
        <section className="relative py-28">
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blood-red rounded-full blur-[200px]" />
          </div>

          <div className="mx-auto relative z-10">
            <div className="container mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-32 items-center">
                <AnimatedSection animation="slide-left">
                  <span className="section-label">{settings.about_mission_label || "Our Mission"}</span>
                  <h2 className="section-title text-[hsl(0,0%,10%)] mb-4">
                    {settings.about_mission_title || "Craft Games With a Rogue Twist"}
                  </h2>
                  <div className="space-y-6 text-[hsl(0,0%,40%)] text-lg leading-relaxed">
                    {missionParagraphs.map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))}
                  </div>
                </AnimatedSection>

                {/* Stats/Highlights */}
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <AnimatedSection key={stat.label} animation="scale" delay={index * 100}>
                      <div className="overflow-hidden group relative p-8 border border-[hsl(0,0%,85%)] bg-[#0c0c0c] transition-all duration-500 hover:-translate-y-1">
                        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-blood-red/80 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out" />
                        <span
                          className={`text-5xl md:text-6xl font-display font-bold ${index === 0 ? "text-blood-red" : "text-[hsl(0,0%,10%)]"}`}
                        >
                          {stat.value}
                        </span>
                        <p className="text-[hsl(0,0%,50%)] mt-3 text-sm uppercase tracking-wider">{stat.label}</p>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section - Horizontal Cards */}
        <section className="bg-white relative py-28">
          <div className="mx-auto">
            <div className="container mx-auto">
              <AnimatedSection animation="fade-up" className="text-center mb-16">
                <span className="section-label">{settings.about_values_label || "What Drives Us"}</span>
                <h2 className="section-title text-[hsl(0,0%,10%)]">
                  {settings.about_values_title || "Our Philosophy"}
                </h2>
              </AnimatedSection>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {valueCards.map((card, index) => (
                  <AnimatedSection
                    key={card.num}
                    animation="fade-up"
                    delay={index * 100}
                    className={index === 2 ? "md:col-span-2 md:flex md:justify-center lg:col-span-1 lg:block" : ""}
                  >
                    <div
                      className={`overflow-hidden group relative p-8 md:p-10 border border-[hsl(0,0%,90%)] bg-[#0c0c0c] transition-all duration-500 hover:-translate-y-1 h-full ${index === 2 ? "md:w-[calc(50%-1rem)] lg:w-auto" : ""}`}
                    >
                      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-blood-red/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <span className="text-blood-red text-5xl font-display font-bold absolute top-4 right-6">
                        {card.num}
                      </span>
                      <h3 className="text-2xl font-display font-bold text-[hsl(0,0%,10%)] mb-4 uppercase tracking-wide relative z-10">
                        {card.title}
                      </h3>
                      <p className="text-[hsl(0,0%,40%)] leading-relaxed relative z-10">{card.desc}</p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team Section - Asymmetric Layout */}
        <section className="py-28 relative">
          <div className="container mx-auto relative z-10">
            <div className="mx-auto">
              <div className="space-y-10 lg:space-y-12">
                {/* Text Block - Full Width Above */}
                <AnimatedSection animation="slide-left" className="max-w-3xl pb-12">
                  <span className="section-label">{settings.about_crew_label || "Meet The Crew"}</span>
                  <h2 className="section-title text-[hsl(0,0%,10%)] mb-4">
                    {settings.about_crew_title || "Your Rogue Captains"}
                  </h2>
                  <p className="text-[hsl(0,0%,40%)] text-lg leading-relaxed">
                    {settings.about_crew_text ||
                      "Kyle, Adam, and Ross are the masterminds behind Rogue Skull Games. United by a shared passion for board gaming, they bring their unique perspectives and expertise to every game they create."}
                  </p>
                </AnimatedSection>

                {/* Crew Grid - Full Container Width */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-24">
                  {displayCrew.map((member, index) => {
                    // Stagger pattern - tablet: right column shifted down, follower pulled flush | desktop: 0, 50px, 0
                    const offsetClass = index % 2 === 1 ? "md:mt-[50px]" : "md:-mt-[50px] lg:mt-0";

                    return (
                      <AnimatedSection key={member.id} animation="fade-up" delay={index * 100} className={offsetClass}>
                        <div className="group relative transition-all duration-500 hover:-translate-y-1 h-[440px] md:h-[550px]">
                          {/* Flag body - clipped into a hanging-flag point */}
                          <div className="absolute inset-0">
                            {/* Image */}
                            {member.photo_url ? (
                              <LazyImage
                                src={member.photo_url}
                                alt={member.name}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-muted/20">
                                <MaterialIcon name="person" className="text-muted-foreground/30 text-6xl" />
                              </div>
                            )}

                            {/* Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-dark/90 via-dark/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
                          </div>

                          {/* Text Overlay */}
                          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                            {member.titles && member.titles.length > 0 && (
                              <div className="mb-1 space-y-0.5">
                                {member.titles.map((title, i) => (
                                  <p
                                    key={i}
                                    className="text-dark-foreground/80 text-sm md:text-sm uppercase tracking-wider"
                                  >
                                    {title}
                                  </p>
                                ))}
                              </div>
                            )}
                            <h3 className="font-display font-bold text-dark-foreground uppercase tracking-wide text-3xl">
                              {member.name}
                            </h3>
                          </div>

                          {/* Hover accent */}
                          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blood-red to-blood-red/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </div>
                      </AnimatedSection>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-28 bg-[hsl(0,0%,96%)] relative overflow-hidden">
          <div className="container mx-auto">
            <AnimatedSection animation="scale" className="max-w-4xl mx-auto text-center">
              <MaterialIcon name="sailing" size="xl" className="text-blood-red mb-6" />
              <h2 className="section-title text-[hsl(0,0%,10%)] mb-6">
                {settings.about_cta_title || "Join the Adventure"}
              </h2>
              <p className="text-[hsl(0,0%,40%)] text-lg mb-10 max-w-2xl mx-auto">
                {settings.about_cta_text ||
                  "Join us on this thrilling journey through the world of tabletop gaming. Every game is crafted to offer a distinct and memorable experience."}
              </p>
              <div className="flex justify-center gap-4">
                {[
                  { icon: Facebook, href: settings.social_facebook, label: "Facebook" },
                  { icon: Instagram, href: settings.social_instagram, label: "Instagram" },
                  { icon: XIcon, href: settings.social_x, label: "X" },
                  { icon: Youtube, href: settings.social_youtube, label: "YouTube" },
                  { icon: Mail, href: settings.social_email ? `mailto:${settings.social_email}` : "", label: "Email" },
                ]
                  .filter((link) => link.href && link.href !== "" && link.href !== "mailto:")
                  .map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      aria-label={`Follow us on ${social.label}`}
                      className="w-12 h-12 rounded-full border border-[hsl(0,0%,70%)] flex items-center justify-center text-[hsl(0,0%,30%)] hover:text-blood-red hover:border-blood-red hover:bg-blood-red/10 transition-all duration-300"
                      target={social.href.startsWith("mailto:") ? "_self" : "_blank"}
                      rel={social.href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
                    >
                      <social.icon size={20} />
                    </a>
                  ))}
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Game Development Progress Section */}
        <GameDevProgressSection />
      </div>
    </MainLayout>
  );
};

export default About;
